/**
 * Input Fields Section Component
 *
 * Displays input fields with their source mappings.
 * Can be placed independently in the node config panel.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Plus, Wand2, Trash2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useNodeFields } from '../hooks/useNodeFields';
import type {
  FieldDefinition,
  FieldType,
  InputFieldMapping,
  ObjectKeyInfo,
  AvailableSource,
  ContextVariable,
  StartVariable,
} from '@/types/workflowFields';
import { getFieldTypeColor, getFieldTypeLabel, isLiteralMapping, isConfigMappingKey } from '@/types/workflowFields';
import { useI18n } from '@/i18n';

interface InputFieldsSectionProps {
  nodeId: string;
  isDisabled?: boolean;
  /** Optional title override */
  title?: string;
  /** Show/hide add field button */
  showAddButton?: boolean;
  /** Show/hide auto-map button */
  showAutoMap?: boolean;
  /** Custom border color class */
  borderColorClass?: string;
  /** Custom background color class */
  bgColorClass?: string;
  /** Available context variables (ctx.xxx) */
  contextVariables?: ContextVariable[];
  /** Available start node variables (var.xxx) */
  startVariables?: StartVariable[];
}

// Field type options for adding custom fields
const FIELD_TYPE_OPTIONS: Array<{ value: FieldType; label: string }> = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
  { value: 'image_url', label: 'Image' },
];

export const InputFieldsSection: React.FC<InputFieldsSectionProps> = ({
  nodeId,
  isDisabled = false,
  title,
  showAddButton = true,
  showAutoMap = true,
  borderColorClass = 'border-blue-200',
  bgColorClass = 'bg-blue-50/50',
  contextVariables = [],
  startVariables = [],
}) => {
  const { t } = useI18n();
  const [showTypePopover, setShowTypePopover] = useState(false);
  const [editingNewFieldKey, setEditingNewFieldKey] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Use provided title or default from i18n
  const displayTitle = title || t('imageWorkflows.inputFieldsSection.title');

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowTypePopover(false);
      }
    };
    if (showTypePopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTypePopover]);

  const {
    nodeTypeSpec,
    customInputFields, // Only user-defined fields, not defaults
    inputMappings,
    availableSources,
    autoMapFromUpstream,
    updateMapping,
    updateMappingKeyPath,
    updateLiteralValue,
    addInputField,
    addInputFields,
    updateInputField,
    removeInputField,
    updateInputMappings,
  } = useNodeFields(nodeId);

  // Always show this section to allow users to add custom input variables
  if (!nodeTypeSpec) {
    return null;
  }

  const handleAutoMap = () => {
    // Auto-map creates new input fields from upstream outputs and maps them
    const { newFields, newMappings } = autoMapFromUpstream();

    if (newFields.length > 0) {
      // Add new fields first
      addInputFields(newFields);
    }

    if (newMappings.length > 0) {
      // Merge with existing mappings (keep existing, add new)
      const existingKeys = new Set(inputMappings.map((m) => m.input_field_key));
      const uniqueNewMappings = newMappings.filter((m) => !existingKeys.has(m.input_field_key));
      updateInputMappings([...inputMappings, ...uniqueNewMappings]);
    }
  };

  const handleAddFieldWithType = (type: FieldType) => {
    // Generate a temporary unique key
    const tempKey = `field_${Date.now()}`;
    const field: FieldDefinition = {
      key: tempKey,
      type,
      label: '',
      required: false,
      indexed: false,
    };
    addInputField(field);
    setShowTypePopover(false);
    // Set this field as being edited
    setEditingNewFieldKey(tempKey);
  };

  const handleNewFieldNameSet = (oldKey: string, newKey: string) => {
    if (newKey.trim()) {
      updateInputField(oldKey, newKey.trim());
    } else {
      // If empty, remove the field
      removeInputField(oldKey);
    }
    setEditingNewFieldKey(null);
  };

  // Get mapping for a field - only return upstream node mappings
  // This section handles external mappings (upstream node outputs → input variables)
  // Config Options section uses prefixed keys (__config__xxx) for internal references
  const getMappingForField = (fieldKey: string): InputFieldMapping | undefined => {
    // First try to find auto-mapped upstream node mapping
    const autoMapping = inputMappings.find((m) =>
      m.input_field_key === fieldKey &&
      m.is_auto_mapped === true &&
      !isConfigMappingKey(m.input_field_key)  // Explicit: ignore config-prefixed keys
    );
    if (autoMapping) return autoMapping;

    // Then try to find manual upstream node mapping
    // Exclude:
    // 1. Config-prefixed keys (__config__xxx) - used by Config Options section
    // 2. Internal sources ('input', 'ctx', 'var', '_literal_') - also Config Options
    return inputMappings.find((m) =>
      m.input_field_key === fieldKey &&
      !isConfigMappingKey(m.input_field_key) &&
      !['input', 'ctx', 'var', '_literal_'].includes(m.source_node_key || '')
    );
  };

  return (
    <div className={`border-2 ${borderColorClass} rounded-lg ${bgColorClass} p-3 space-y-3`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-200">
            <ArrowLeft className="w-3 h-3 text-blue-600" />
          </div>
          <span className="text-sm font-medium text-blue-700">{displayTitle}</span>
          <span className="text-xs text-blue-400">({customInputFields.length})</span>
        </div>
        <div className="flex items-center gap-1">
          {showAutoMap && availableSources.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleAutoMap}
              disabled={isDisabled}
              className="h-7 px-2 text-xs text-blue-600 hover:bg-blue-100"
            >
              <Wand2 className="w-3 h-3 mr-1" />
              {t('imageWorkflows.inputFieldsSection.autoMap')}
            </Button>
          )}
          {showAddButton && (
            <div className="relative" ref={popoverRef}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowTypePopover(!showTypePopover)}
                disabled={isDisabled}
                className="h-7 px-2 text-blue-600 hover:bg-blue-100"
              >
                <Plus className="w-3 h-3" />
              </Button>
              {/* Type Selection Popover */}
              {showTypePopover && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[120px]">
                  {FIELD_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleAddFieldWithType(opt.value)}
                      className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getFieldTypeColor(opt.value) }}
                      />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom Input Fields List */}
      <div className="space-y-2">
        {customInputFields.length === 0 ? (
          <p className="text-xs text-gray-400 italic text-center py-2">
            {t('imageWorkflows.inputFieldsSection.noFields')}
          </p>
        ) : (
          customInputFields.map((field) => (
            <InputFieldRow
              key={field.key}
              field={field}
              mapping={getMappingForField(field.key)}
              availableSources={availableSources}
              contextVariables={contextVariables}
              startVariables={startVariables}
              onUpdateMapping={(sourceNodeKey, sourceOutputKey, keyPath) =>
                updateMapping(field.key, sourceNodeKey, sourceOutputKey, keyPath)
              }
              onUpdateLiteralValue={(value) => updateLiteralValue(field.key, value)}
              onUpdateKeyPath={(keyPath) => updateMappingKeyPath(field.key, keyPath)}
              onUpdateFieldName={(newKey) => updateInputField(field.key, newKey)}
              onNewFieldNameSet={(newKey) => handleNewFieldNameSet(field.key, newKey)}
              onRemoveField={() => removeInputField(field.key)}
              isDisabled={isDisabled}
              isEditingNew={editingNewFieldKey === field.key}
            />
          ))
        )}
      </div>
      {/* Usage hint */}
      {customInputFields.length > 0 && (
        <p className="text-xs text-gray-500">
          💡 {t('imageWorkflows.inputFieldsSection.usageHint')}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

interface InputFieldRowProps {
  field: FieldDefinition;
  mapping?: InputFieldMapping;
  availableSources: AvailableSource[];
  contextVariables?: ContextVariable[];
  startVariables?: StartVariable[];
  onUpdateMapping: (sourceNodeKey: string, sourceOutputKey: string, keyPath?: string) => void;
  onUpdateLiteralValue: (value: string) => void;
  onUpdateKeyPath: (keyPath: string | undefined) => void;
  onUpdateFieldName: (newKey: string) => void;
  onNewFieldNameSet?: (newKey: string) => void;
  onRemoveField?: () => void;
  isDisabled?: boolean;
  isEditingNew?: boolean;
}

type InputMode = 'mapping' | 'literal';

const InputFieldRow: React.FC<InputFieldRowProps> = ({
  field,
  mapping,
  availableSources,
  contextVariables = [],
  startVariables = [],
  onUpdateMapping,
  onUpdateLiteralValue,
  onUpdateKeyPath,
  onUpdateFieldName,
  onNewFieldNameSet,
  onRemoveField,
  isDisabled,
  isEditingNew = false,
}) => {
  const { t } = useI18n();
  // Determine initial mode based on current mapping
  const getInitialMode = (): InputMode => {
    if (mapping && isLiteralMapping(mapping)) {
      return 'literal';
    }
    return 'mapping';
  };

  const [mode, setMode] = useState<InputMode>(getInitialMode);
  const [localKeyPath, setLocalKeyPath] = useState(mapping?.source_key_path || '');
  const [localLiteralValue, setLocalLiteralValue] = useState(mapping?.literal_value || '');
  const [isEditingName, setIsEditingName] = useState(isEditingNew);
  const [localFieldName, setLocalFieldName] = useState(isEditingNew ? '' : field.key);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Auto focus and select when editing field name
  useEffect(() => {
    if ((isEditingNew || isEditingName) && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingNew, isEditingName]);

  // For mapping mode
  const currentValue = mapping && !isLiteralMapping(mapping)
    ? `${mapping.source_node_key}::${mapping.source_output_key}`
    : '';

  // Get the type of the currently selected output
  const getSelectedOutputType = (): FieldType | undefined => {
    if (!mapping || isLiteralMapping(mapping)) return undefined;
    const source = availableSources.find((s) => s.node_key === mapping.source_node_key);
    if (!source) return undefined;
    const output = source.outputs.find((o) => o.key === mapping.source_output_key);
    return output?.type;
  };

  // Get the object keys of the currently selected output (for object type)
  const getSelectedOutputObjectKeys = (): ObjectKeyInfo[] | undefined => {
    if (!mapping || isLiteralMapping(mapping)) return undefined;
    const source = availableSources.find((s) => s.node_key === mapping.source_node_key);
    if (!source) return undefined;
    const output = source.outputs.find((o) => o.key === mapping.source_output_key);
    return output?.object_keys;
  };

  const selectedOutputType = getSelectedOutputType();
  const isObjectType = selectedOutputType === 'object';
  const objectKeys = getSelectedOutputObjectKeys();

  const handleSelectionChange = (combinedValue: string) => {
    if (!combinedValue) {
      // Clear selection - switch to literal mode with empty value
      setMode('literal');
      setLocalLiteralValue('');
      return;
    }

    // Check if selecting literal option
    if (combinedValue === '_literal_::value') {
      setMode('literal');
      setLocalLiteralValue('');
      return;
    }

    // Regular mapping selection
    setMode('mapping');
    const [nodeKey, outputKey] = combinedValue.split('::');
    if (nodeKey && outputKey) {
      setLocalKeyPath('');
      onUpdateMapping(nodeKey, outputKey, undefined);
    }
  };

  const handleLiteralValueChange = (value: string) => {
    setLocalLiteralValue(value);
  };

  const handleLiteralValueBlur = () => {
    onUpdateLiteralValue(localLiteralValue);
  };

  const handleLiteralValueKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLiteralValueBlur();
    }
  };

  const handleKeyPathChange = (value: string) => {
    setLocalKeyPath(value);
  };

  const handleKeyPathBlur = () => {
    const trimmed = localKeyPath.trim();
    onUpdateKeyPath(trimmed || undefined);
  };

  const handleKeyPathKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleKeyPathBlur();
    }
  };

  const handleFieldNameClick = () => {
    if (!isDisabled && !isEditingNew) {
      setIsEditingName(true);
      setLocalFieldName(field.key);
    }
  };

  const handleFieldNameBlur = () => {
    const trimmed = localFieldName.trim();
    if (isEditingNew && onNewFieldNameSet) {
      // For new fields, call the special handler
      onNewFieldNameSet(trimmed);
    } else {
      setIsEditingName(false);
      if (trimmed && trimmed !== field.key) {
        onUpdateFieldName(trimmed);
      }
    }
  };

  const handleFieldNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFieldNameBlur();
    } else if (e.key === 'Escape') {
      if (isEditingNew && onNewFieldNameSet) {
        // Cancel new field creation
        onNewFieldNameSet('');
      } else {
        setIsEditingName(false);
        setLocalFieldName(field.key);
      }
    }
  };

  // Sync local state when mapping changes externally
  React.useEffect(() => {
    setLocalKeyPath(mapping?.source_key_path || '');
    setLocalLiteralValue(mapping?.literal_value || '');
    if (mapping && isLiteralMapping(mapping)) {
      setMode('literal');
    } else if (mapping) {
      setMode('mapping');
    }
  }, [mapping]);

  // Sync field name when field changes
  React.useEffect(() => {
    setLocalFieldName(field.key);
  }, [field.key]);

  // Filter out start node from available sources (those are shown in ctx/var sections)
  const filteredSources = availableSources.filter((s) => s.node_type !== 'start');
  const hasSources = filteredSources.length > 0 || contextVariables.length > 0 || startVariables.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 p-2 bg-white rounded border text-sm">
        {/* Field info - clickable to edit, with type shown below */}
        <div className="flex flex-col min-w-0 flex-shrink-0 w-28">
          <div className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: getFieldTypeColor(field.type) }}
            />
            {isEditingName || isEditingNew ? (
              <input
                ref={nameInputRef}
                type="text"
                value={localFieldName}
                onChange={(e) => setLocalFieldName(e.target.value)}
                onBlur={handleFieldNameBlur}
                onKeyDown={handleFieldNameKeyDown}
                placeholder={t('imageWorkflows.inputFieldsSection.variableName')}
                className="flex-1 min-w-0 h-6 px-1 text-sm font-medium border border-blue-400 rounded bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400 placeholder:font-normal"
              />
            ) : (
              <span
                onClick={handleFieldNameClick}
                className="font-medium truncate text-gray-700 cursor-pointer hover:text-blue-600 hover:underline"
                title={t('imageWorkflows.inputFieldsSection.clickToEdit').replace('{key}', field.key)}
              >
                {field.label || field.key}
              </span>
            )}
            {field.required && <span className="text-red-500 text-xs">*</span>}
          </div>
          {/* Field type shown below */}
          <span className="text-[10px] text-gray-400 ml-4">{getFieldTypeLabel(field.type)}</span>
        </div>

        {/* Arrow */}
        <span className="text-gray-400 flex-shrink-0">←</span>

        {/* Content based on mode */}
        <div className="flex-1 min-w-0">
          {mode === 'literal' ? (
            /* Literal value input with inline toggle */
            <div className="relative flex items-center">
              <input
                type="text"
                value={localLiteralValue}
                onChange={(e) => handleLiteralValueChange(e.target.value)}
                onBlur={handleLiteralValueBlur}
                onKeyDown={handleLiteralValueKeyDown}
                placeholder={t('imageWorkflows.inputFieldsSection.inputFixedValue')}
                disabled={isDisabled}
                className="w-full h-8 pl-2 pr-14 text-sm border border-amber-300 rounded bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:bg-gray-100 placeholder:text-gray-400"
              />
              {hasSources && (
                <button
                  onClick={() => setMode('mapping')}
                  disabled={isDisabled}
                  className="absolute right-1 px-2 h-6 text-xs text-blue-600 bg-white hover:bg-blue-50 rounded border border-blue-200 shadow-sm"
                  title={t('imageWorkflows.inputFieldsSection.switchToSelect')}
                >
                  {t('imageWorkflows.inputFieldsSection.switchToSelect')}
                </button>
              )}
            </div>
          ) : (
            /* Mapping selector */
            <select
              value={currentValue}
              onChange={(e) => handleSelectionChange(e.target.value)}
              disabled={isDisabled || !hasSources}
              className="w-full h-8 px-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="">{t('imageWorkflows.inputFieldsSection.selectSource')}</option>
              {/* Fixed Value Option */}
              <option value="_literal_::value">✏️ {t('imageWorkflows.inputFieldsSection.fixedValue')}</option>
              {/* Context Variables */}
              {contextVariables.length > 0 && (
                <optgroup label={`🌐 ${t('imageWorkflows.inputFieldsSection.contextVariables')}`}>
                  {contextVariables.map((ctxVar) => (
                    <option key={`ctx::${ctxVar.key}`} value={`ctx::${ctxVar.key}`}>
                      {ctxVar.label} (ctx.{ctxVar.key})
                    </option>
                  ))}
                </optgroup>
              )}
              {/* Start Variables */}
              {startVariables.length > 0 && (
                <optgroup label={`📝 ${t('imageWorkflows.inputFieldsSection.customVariables')}`}>
                  {startVariables.map((startVar) => (
                    <option key={`var::${startVar.key}`} value={`var::${startVar.key}`}>
                      {startVar.key} (var.{startVar.key})
                    </option>
                  ))}
                </optgroup>
              )}
              {/* Upstream Node Outputs (excluding start node - those are in ctx/var sections) */}
              {filteredSources.map((source) => (
                <optgroup key={source.node_key} label={`📦 ${source.node_label}`}>
                  {source.outputs.map((output) => (
                    <option
                      key={`${source.node_key}::${output.key}`}
                      value={`${source.node_key}::${output.key}`}
                    >
                      {output.label || output.key}
                      {output.type === 'object' && ` ${t('imageWorkflows.inputFieldsSection.selectableKey')}`}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
        </div>

        {/* Delete field button */}
        {onRemoveField && (
          <button
            onClick={onRemoveField}
            disabled={isDisabled}
            className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
            title={t('imageWorkflows.inputFieldsSection.deleteField')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Key Path Input - shown only for object type outputs in mapping mode */}
      {mode === 'mapping' && isObjectType && mapping && (
        <div className="ml-28 flex items-center gap-2">
          <span className="text-xs text-gray-500">{t('imageWorkflows.inputFieldsSection.extractKey')}:</span>
          {objectKeys && objectKeys.length > 0 ? (
            // Has schema info: show dropdown selector
            <select
              value={localKeyPath}
              onChange={(e) => {
                const value = e.target.value;
                setLocalKeyPath(value);
                onUpdateKeyPath(value || undefined);
              }}
              disabled={isDisabled}
              className="flex-1 h-7 px-2 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">{t('imageWorkflows.inputFieldsSection.entireObject')}</option>
              {objectKeys.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.label || k.key} ({getFieldTypeLabel(k.type)})
                </option>
              ))}
            </select>
          ) : (
            // No schema info: show text input as fallback
            <input
              type="text"
              value={localKeyPath}
              onChange={(e) => handleKeyPathChange(e.target.value)}
              onBlur={handleKeyPathBlur}
              onKeyDown={handleKeyPathKeyDown}
              placeholder={t('imageWorkflows.inputFieldsSection.keyPathPlaceholder')}
              disabled={isDisabled}
              className="flex-1 h-7 px-2 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 placeholder:text-gray-400"
            />
          )}
          {localKeyPath && (
            <span className="text-xs text-green-600">
              → {mapping.source_output_key}.{localKeyPath}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default InputFieldsSection;
